<template>
  <div class="rm rm-theme wm">
    <!-- Početna: izbor taba -->
    <template v-if="!currentKey">
      <div class="rm-home-header">
        <span v-if="restaurantName" class="rm-muted">{{ restaurantName }}</span>
        <h1 class="rm-home-header__title">{{ $t("routes.warehouse") }}</h1>
        <button v-if="warehouses.length" type="button" class="rm-view-switch" data-open="filters" @click="filterSheet = true">
          {{ warehouseLabel || $t("warehouse_shell.pick_warehouse") }}
          <v-icon size="18" color="#9a3412">mdi-chevron-down</v-icon>
        </button>
      </div>

      <div class="rm-chip-row rm-home-periods">
        <button
          v-for="preset in quickPresets"
          :key="preset.key"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': activePresetKey === preset.key }"
          :aria-pressed="String(activePresetKey === preset.key)"
          @click="setPeriod(preset)"
        >
          {{ presetChipLabel(preset) }}
        </button>
        <button
          v-if="!activeQuickPreset"
          type="button"
          class="rm-chip rm-chip--lg rm-chip--active rm-num"
          aria-pressed="true"
          @click="periodSheet = true"
        >
          {{ periodShort }}
        </button>
        <button
          type="button"
          class="rm-chip rm-chip--lg rm-chip--icon"
          :aria-label="$t('report_mobile.choose_period')"
          @click="periodSheet = true"
        >
          <v-icon size="20" color="#1c1917">mdi-calendar-blank-outline</v-icon>
        </button>
      </div>

      <div v-if="ready && !warehouses.length" class="rm-empty">
        <v-icon size="40" color="#8a817a">mdi-warehouse</v-icon>
        <b>{{ $t("no_warehouses") }}</b>
        <span>{{ $t("no_warehouses_added_yet") }}</span>
        <button type="button" class="rm-btn rm-btn--primary wm-new" @click="warehouseDialog = true">
          <v-icon size="20" color="#ffffff">mdi-plus</v-icon>
          {{ $t("new_warehouse") }}
        </button>
      </div>

      <div class="rm-home-groups">
        <section v-for="group in menuGroups" :key="group.key" class="rm-home-group">
          <h2 v-if="group.title" class="rm-section-title">{{ group.title }}</h2>
          <div class="rm-card">
            <button
              v-for="item in group.items"
              :key="item.key"
              type="button"
              class="rm-menu-row"
              :data-key="item.key"
              @click="openScreen(item.key)"
            >
              <span class="rm-menu-row__icon">
                <v-icon size="20" color="#c2410c">{{ item.icon }}</v-icon>
              </span>
              <span class="rm-menu-row__text">
                <span class="rm-menu-row__title">{{ item.title }}</span>
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </div>
        </section>
      </div>
    </template>

    <!-- Pojedinačni tab -->
    <template v-else>
      <div class="rm-detail-top">
        <div class="rm-detail-header">
          <button type="button" class="rm-icon-btn" :aria-label="$t('report_mobile.back')" @click="closeScreen">
            <v-icon size="24" color="#1c1917">mdi-chevron-left</v-icon>
          </button>
          <div class="rm-detail-header__text">
            <h1 class="rm-detail-header__title">{{ screen.title }}</h1>
            <span class="rm-muted rm-detail-header__sub">{{ headerSubtitle }}</span>
          </div>
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('search')"
            :aria-pressed="String(searchOpen)"
            @click="toggleSearch"
          >
            <v-icon size="22" color="#1c1917">mdi-magnify</v-icon>
          </button>
          <button
            v-if="hasFilters"
            type="button"
            class="rm-icon-btn"
            :class="{ 'rm-g-active': filtersActive }"
            :aria-label="$t('filters')"
            data-open="filters"
            @click="filterSheet = true"
          >
            <v-icon size="22" :color="filtersActive ? '#9a3412' : '#1c1917'">mdi-tune-variant</v-icon>
          </button>
          <button type="button" class="rm-icon-btn" :aria-label="$t('report_mobile.share')" data-open="actions" @click="actionsSheet = true">
            <v-icon size="22" color="#1c1917">mdi-export-variant</v-icon>
          </button>
        </div>

        <div v-if="searchOpen" class="rm-detail-search">
          <label class="rm-search">
            <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
            <span class="rm-visually-hidden">{{ $t("search") }}</span>
            <input ref="searchInput" v-model="search" type="search" :placeholder="$t('search')" />
          </label>
        </div>

        <div class="rm-chip-row">
          <button v-if="screenFilters.period === 'range'" type="button" class="rm-chip" data-open="period" @click="periodSheet = true">
            <span class="rm-num">{{ periodShort }}</span>
            <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
          </button>
          <span v-else-if="screenFilters.period === 'day'" class="rm-chip wm-chip-static">
            <v-icon size="16" color="#5f5852">mdi-calendar-blank-outline</v-icon>
            <span class="rm-num">{{ periodShort }}</span>
          </span>
          <button
            v-if="screenFilters.warehouse && warehouses.length > 1"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': !allWarehousesSelected }"
            data-open="filters"
            @click="filterSheet = true"
          >
            {{ warehouseLabel || $t("warehouse_shell.pick_warehouse") }}
            <v-icon size="16" :color="allWarehousesSelected ? '#5f5852' : '#9a3412'">mdi-chevron-down</v-icon>
          </button>
        </div>
      </div>

      <div v-if="!ready" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else-if="!warehouses.length" class="rm-empty">
        <v-icon size="40" color="#8a817a">mdi-warehouse</v-icon>
        <b>{{ $t("no_warehouses") }}</b>
        <span>{{ $t("no_warehouses_added_yet") }}</span>
        <button type="button" class="rm-btn rm-btn--primary wm-new" @click="warehouseDialog = true">
          <v-icon size="20" color="#ffffff">mdi-plus</v-icon>
          {{ $t("new_warehouse") }}
        </button>
      </div>
      <div v-else-if="needsWarehouse" class="rm-empty">
        <v-icon size="40" color="#8a817a">mdi-warehouse</v-icon>
        <b>{{ $t("warehouse_shell.pick_warehouse") }}</b>
        <span>{{ $t("warehouse_shell.pick_warehouse_text") }}</span>
      </div>
      <div v-else-if="screen.legacy" class="rm-legacy">
        <component :is="screen.component" :key="currentKey"></component>
      </div>
      <component
        :is="screen.component"
        v-else
        :key="currentKey"
        @toast="showToast"
      ></component>
    </template>

    <report-mobile-period-sheet
      v-model="periodSheet"
      :from="from"
      :to="to"
      :presets="presets"
      @apply="setPeriod"
    ></report-mobile-period-sheet>

    <warehouse-mobile-filter-sheet
      v-model="filterSheet"
      :config="sheetConfig"
      :warehouse-items="warehouseItems"
      :selected-warehouse-ids="selectedWarehouseIds"
      :view-items="viewItems"
      :show-type="filters.showType"
      :shifts="filters.selectedShifts"
      :doc-type-items="docTypeItems"
      :document-type="filters.documentType"
      :status-items="statusItems"
      :status="filters.status"
      :supplier-items="supplierItems"
      :selected-supplier-ids="selectedSupplierIds"
      :grocery-items="groceryItems"
      :grocery-code="filters.groceryCode ? filters.groceryCode.code : null"
      @apply="applyFilters"
    ></warehouse-mobile-filter-sheet>

    <ui-sheet v-model="actionsSheet" :label="$t('report_mobile.more_options')">
      <ul class="rm-list rm-actions">
        <li>
          <button type="button" class="rm-actions__row" data-action="print" @click="runAction('print')">
            <v-icon size="22" color="#1c1917">mdi-printer-outline</v-icon>
            {{ $t("report_mobile.print_pdf") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" data-action="excel" @click="runAction('excel')">
            <v-icon size="22" color="#15803d">mdi-microsoft-excel</v-icon>
            {{ $t("report_mobile.download_excel") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" data-action="reload" @click="runAction('reload')">
            <v-icon size="22" color="#1c1917">mdi-refresh</v-icon>
            {{ $t("report_mobile.refresh") }}
          </button>
        </li>
      </ul>
    </ui-sheet>

    <div v-if="toast" class="wm-toast" role="status">{{ toast }}</div>

    <warehouse-dialog v-model="warehouseDialog" :edited-item="editedWarehouse" @create="onWarehouseCreated" />
  </div>
</template>

<script>
import "@/components/report/mobile/reportMobileShell.scss";
import "./warehouseMobile.scss";
import ReportMobilePeriodSheet from "@/components/report/mobile/ReportMobilePeriodSheet.vue";
import { findPresetKey, formatRange, formatShortDate } from "@/components/report/shared/periods";
import WarehouseDialog from "@/components/warehouse/dialogs/WarehouseDialog.vue";
import hasWarehouseShellState from "../hasWarehouseShellState";
import { buildWarehouseScreens } from "../warehouseMenu";
import { WAREHOUSE_QUERY } from "../warehouseQuery";
import WarehouseMobileFilterSheet from "./WarehouseMobileFilterSheet.vue";

const QUICK_KEYS = ["today", "tomorrow", "this_week"];

/**
 * Magacin na telefonu: početna sa listom tabova po grupama, pa tab sa zaglavljem, periodom i filterima u donjoj
 * kartici. Trebovanje je novi ekran (kartice, donja traka sa radnjom); ostali tabovi su stari tabovi, koji već
 * imaju prikaz za telefon. Izbori su u store-u `warehouse/filters` (kao do sada), tab i period u adresi.
 */
export default {
  name: "WarehouseMobile",
  mixins: [hasWarehouseShellState],
  components: { ReportMobilePeriodSheet, WarehouseMobileFilterSheet, WarehouseDialog },
  data: () => ({
    searchOpen: false,
    periodSheet: false,
    filterSheet: false,
    actionsSheet: false,
    pushedScreens: 0,
    toast: "",
  }),
  computed: {
    homeWithoutTab() {
      return true;
    },
    screens() {
      return buildWarehouseScreens((key, params) => this.$t(key, params), { mobile: true });
    },
    restaurantName() {
      return this.toLatin(this.$store.state.restaurant?.name || "");
    },
    quickPresets() {
      return this.presets.filter((preset) => QUICK_KEYS.includes(preset.key));
    },
    activePresetKey() {
      return findPresetKey(this.presets, this.from, this.to);
    },
    activeQuickPreset() {
      return QUICK_KEYS.includes(this.activePresetKey);
    },
    periodShort() {
      if (this.activePresetKey && this.from !== this.to) {
        return this.$t(`report_mobile.presets.${this.activePresetKey}`);
      }
      if (this.activePresetKey) {
        return `${this.$t(`report_mobile.presets.${this.activePresetKey}`)} · ${formatShortDate(this.from)}`;
      }
      return formatRange(this.from, this.to);
    },
    allWarehousesSelected() {
      return this.warehouses.length > 0 && this.filters.warehouse.length === this.warehouses.length;
    },
    /** Na početnoj se bira samo magacin; u tabu svi izbori koje taj tab ima (bez perioda, koji ima svoju karticu). */
    sheetConfig() {
      return this.currentKey ? this.screenFilters : { warehouse: true };
    },
    hasFilters() {
      const { period, ...rest } = this.screenFilters;
      return Object.values(rest).some(Boolean);
    },
    filtersActive() {
      const config = this.screenFilters;
      return Boolean(
        (config.warehouse && !this.allWarehousesSelected) ||
          (config.status && this.filters.status) ||
          (config.docType && this.filters.documentType) ||
          (config.grocery && this.filters.groceryCode) ||
          (config.suppliers && this.filters.suppliers.length < this.suppliers.length)
      );
    },
  },
  watch: {
    currentKey() {
      this.searchOpen = Boolean(this.search);
      this.filterSheet = false;
      this.actionsSheet = false;
      window.scrollTo(0, 0);
    },
  },
  created() {
    this.searchOpen = Boolean(this.search);
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
  },
  methods: {
    presetChipLabel(preset) {
      const label = this.$t(`report_mobile.presets.${preset.key}`);
      if (this.activePresetKey === preset.key && preset.from === preset.to) {
        return `${label} · ${formatShortDate(preset.from)}`;
      }
      return label;
    },
    openScreen(key) {
      if (!this.menuKeys.includes(key) || key === this.currentKey) {
        return;
      }
      this.pushedScreens += 1;
      this.$router.push({ query: { ...this.$route.query, [WAREHOUSE_QUERY.tab]: key } }).catch(() => {});
    },
    closeScreen() {
      // Tab otvoren sa ove stranice → nazad kroz istoriju (radi i hardverski "nazad");
      // direktan link (?t=...) → zameni adresu, da ne izađemo iz magacina.
      if (this.pushedScreens > 0) {
        this.pushedScreens -= 1;
        this.$router.back();
        return;
      }
      const query = { ...this.$route.query };
      delete query[WAREHOUSE_QUERY.tab];
      this.$router.replace({ query }).catch(() => {});
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      } else {
        this.search = "";
      }
    },
    applyFilters(draft) {
      const config = this.sheetConfig;
      if (config.warehouse) {
        this.setWarehouseIds(draft.warehouseIds);
      }
      if (config.view) {
        this.setFilterValue("showType", draft.showType);
      }
      if (config.shifts) {
        this.setShifts(draft.shifts);
      }
      if (config.docType) {
        this.setFilterValue("documentType", draft.documentType);
      }
      if (config.status) {
        this.setFilterValue("status", draft.status);
      }
      if (config.suppliers) {
        this.setSupplierIds(draft.supplierIds);
      }
      if (config.grocery) {
        this.setGroceryCode(draft.groceryCode);
      }
    },
    runAction(action) {
      this.actionsSheet = false;
      if (action === "print") {
        this.print();
      } else if (action === "excel") {
        this.excel();
      } else {
        this.reload();
      }
    },
    showToast(text) {
      this.toast = text;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = "";
      }, 3000);
    },
  },
};
</script>
